import React, { useEffect, useState } from 'react';
import { Tabs, Row, Col, Button, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import _ from 'lodash';
import useTemplateCatalogStore from '../../stores/useTemplateCatalogStore';
import resources from '../../reducers/templates/resources';
import session from '../../utils/session';
import config from '../../config';
import mixpanel from '../../utils/mixpanel';
import MyTemplates from './MyTemplates';
import CatalogBrowser from './CatalogBrowser';
import CreateTemplateModal from './CreateTemplateModal';
import EditSequenceModal from './EditSequenceModal';
import PageTourButton from '../guide/PageTourButton';
import { getTemplatesSteps } from '../guide/toursteps';

const { Title,Text } = Typography;

const Templates = () => {
    const {
        singleStepItems,
        ownedSequenceItems,
        loading,
        fetchCatalog,
        fetchEnrollmentCount,
        deleteSequence,
        duplicateTemplate,
        copyTemplateToUser,
    } = useTemplateCatalogStore();

    const myItems = [...singleStepItems, ...ownedSequenceItems];

    const [activeTab, setActiveTab] = useState('my-templates');
    const [modalOpen, setModalOpen] = useState(false);
    const [editTemplate, setEditTemplate] = useState(null);
    const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
    const [editSequence, setEditSequence] = useState(null);

    const userData = session.get('userData');
    const userRole = userData.role;
    const isAdmin = userRole === config.userRoles.ghAdmin || userRole === config.userRoles.companyAdmin;

    useEffect(() => {
        fetchCatalog();
    }, []);

    // Get all unique categories for the autocomplete
    const existingCategories = _.uniq(
        [...singleStepItems, ...ownedSequenceItems]
            .map(t => t.category)
            .filter(Boolean)
    );

    // --- Handlers ---

    const handleCreateTemplate = () => {
        setEditTemplate(null);
        setModalOpen(true);
    };

    const handleEditTemplate = (item) => {
        setEditTemplate(item._raw || {
            id: item.id,
            name: item.title,
            details: item.message,
            category: item.category,
            privacy: item.privacy,
            user: item.owner,
        });
        setModalOpen(true);
    };

    const handleEditSequence = (item) => {
        setEditSequence(item);
        setSequenceModalOpen(true);
    };

    const handleDuplicateTemplate = async (item) => {
        if (item.type === 'multi-step') {
            await duplicateTemplate(item.id, item.title, item.description, item.category, { privacy: 'private' });
            mixpanel.track('sequence:duplicate');
        } else {
            await copyTemplateToUser(item, { privacy: 'private', nameSuffix: ' (Copy)' });
            mixpanel.track('template:duplicate');
        }
    };

    const handleDeleteTemplate = async (item) => {
        if (item.type === 'multi-step') {
            try {
                await deleteSequence(item.id);
                mixpanel.track('sequence:delete');
                message.success('Sequence deleted');
            } catch {
                message.error('Failed to delete sequence');
            }
            return;
        }
        try {
            const response = await resources.deleteTemplate(item.id);
            if (response.statusCode === 200) {
                mixpanel.track('template:delete');
                message.success('Template deleted');
                fetchCatalog();
            } else {
                message.error('Internal server error. Please try again later.');
            }
        } catch {
            message.error('Failed to delete template');
        }
    };

    const handleModalSuccess = () => {
        fetchCatalog();
    };

    const tabItems = [
        {
            key: 'my-templates',
            label: `My Templates (${myItems.length})`,
            children: (
                <MyTemplates
                    items={myItems}
                    loading={loading}
                    userData={userData}
                    isAdmin={isAdmin}
                    onEditTemplate={handleEditTemplate}
                    onEditSequence={handleEditSequence}
                    onDeleteTemplate={handleDeleteTemplate}
                    onDuplicateTemplate={handleDuplicateTemplate}
                    onFetchEnrollmentCount={fetchEnrollmentCount}
                />
            ),
        },
        {
            key: 'catalog',
            label: 'Template Library',
            children: (
                <CatalogBrowser isAdmin={isAdmin} />
            ),
        },
    ];

    return (
        <div className="gh-compact-page" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="gh-compact-panel" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Gradient banner header with tab bar */}
                <div style={{
                    background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                    border: '1px solid #d9f7be',
                    padding: '20px 20px 0',
                }}>
                    <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: 12 }}>
                        <Col>
                            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
                                Templates <PageTourButton steps={getTemplatesSteps()} />
                            </Title>
                            <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                Quickly engage contacts with your pre-set templates and flows
                            </Text>
                        </Col>
                        {activeTab === 'my-templates' && (
                            <Col className="tour-templates-create">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleCreateTemplate}
                                >
                                    Create New Template
                                </Button>
                            </Col>
                        )}
                    </Row>
                    <Tabs
                        className="tour-templates-tabs"
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems.map(t => ({ key: t.key, label: t.label }))}
                        style={{ marginBottom: 0 }}
                    />
                </div>

                {/* Tab content rendered outside the header */}
                <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {tabItems.find(t => t.key === activeTab)?.children}
                </div>
                <CreateTemplateModal
                    open={modalOpen}
                    onClose={() => { setModalOpen(false); setEditTemplate(null); }}
                    onSuccess={handleModalSuccess}
                    editTemplate={editTemplate}
                    existingCategories={existingCategories}
                />
                <EditSequenceModal
                    open={sequenceModalOpen}
                    onClose={() => { setSequenceModalOpen(false); setEditSequence(null); }}
                    onSuccess={handleModalSuccess}
                    sequence={editSequence}
                    existingCategories={existingCategories}
                />
            </div>
        </div>
    );
};

export default Templates;
